<template>
  <div class="metric-card" :data-test="`metric-${def.id}`">
    <div class="metric-card-head">
      <span class="metric-id-cell">
        <span class="metric-id mono">{{ def.id }}</span>
        <button
          type="button"
          class="info-btn"
          :aria-label="`What does ${def.id} mean?`"
          :title="`What does ${def.id} mean?`"
          @click.stop="$emit('open', def, envelope)"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9.5" />
            <line x1="12" y1="11" x2="12" y2="17" />
            <circle cx="12" cy="7.5" r="0.9" fill="currentColor" stroke="none" />
          </svg>
        </button>
      </span>
      <span v-if="rag" :class="['rag-chip', rag]">{{ rag.toUpperCase() }}</span>
    </div>

    <div class="metric-label">{{ def.label }}</div>

    <!-- Polymorphic value -->
    <div v-if="!hasValue" class="metric-value composite">—</div>
    <Ratio3Bar
      v-else-if="def.kind === 'ratio3' && Array.isArray(rawValue)"
      :parts="rawValue as number[]"
      :part-labels="def.parts as [string, string, string] | undefined"
    />
    <DictBars
      v-else-if="def.kind === 'dict' && isPlainObject(rawValue)"
      :dict="rawValue as Record<string, number>"
      :unit="def.unit"
    />
    <div
      v-else-if="def.kind === 'bool'"
      class="metric-value"
      :style="{ color: boolReady ? 'var(--rag-green, #15803d)' : 'var(--rag-red, #991b1b)' }"
    >
      {{ boolReady ? "READY" : "NOT READY" }}
    </div>
    <div v-else-if="def.kind === 'spPct' && isPlainObject(rawValue)" class="metric-value-block">
      <div class="metric-value">{{ fmtInt((rawValue as Record<string, number>).sp) }}</div>
      <div class="subtle mono">{{ spPctText }}</div>
    </div>
    <div v-else-if="def.kind === 'share' && isPlainObject(rawValue)" class="metric-value">
      {{ fmtInt((rawValue as Record<string, number>).sp) }}
      <span v-if="shareText" class="subtle">{{ shareText }}</span>
    </div>
    <div v-else-if="def.kind === 'pct'" class="metric-value">{{ pct(rawValue as number) }}</div>
    <div v-else-if="def.kind === 'int'" class="metric-value">{{ fmtInt(rawValue as number) }}</div>
    <div v-else-if="def.kind === 'days'" class="metric-value">
      {{ fmtNum(rawValue as number, 1) }}<span class="subtle d-suffix">d</span>
    </div>
    <div v-else class="metric-value composite">{{ jsonClip }}</div>

    <!-- Sparkline (scalar metrics only) -->
    <div v-if="hasSparkline" class="metric-spark" data-test="metric-spark">
      <Sparkline :series="sparkSeries" :stroke="sparkStroke" :width="120" :height="28" />
    </div>
    <div v-else-if="canSpark" class="metric-spark metric-spark-empty" />

    <div class="metric-source">
      <span>{{ source }}</span>
      <span :class="['rag-dot', rag || 'none']" />
    </div>
    <div :class="['metric-bar', rag || 'none']" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { MetricEnvelope, SprintListItem } from "@/api/types";
import type { MetricDef } from "@/lib/metricDefs";
import { fmtInt, fmtNum, pct } from "@/lib/format";
import Ratio3Bar from "./Ratio3Bar.vue";
import DictBars from "./DictBars.vue";
import Sparkline from "./Sparkline.vue";

const props = defineProps<{
  def: MetricDef;
  envelope: MetricEnvelope | null | undefined;
  /** Chronological list (oldest → newest) of recent sprints for the sparkline. */
  sprintsForSpark?: SprintListItem[];
}>();

defineEmits<{
  (e: "open", def: MetricDef, env: MetricEnvelope | null | undefined): void;
}>();

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

const rawValue = computed(() => props.envelope?.value);
const rag = computed(() => props.envelope?.rag ?? null);

const hasValue = computed(() => {
  const v = rawValue.value;
  return v != null && !(typeof v === "number" && Number.isNaN(v));
});

const boolReady = computed(() => rawValue.value === true);

const spPctText = computed(() => {
  const v = rawValue.value as Record<string, number> | undefined;
  if (!v) return "";
  const p = v.pct ?? v.percent ?? null;
  return p == null ? "" : pct(p);
});

const shareText = computed(() => {
  const v = rawValue.value as Record<string, number> | undefined;
  if (!v) return "";
  const sh = v.share ?? null;
  return sh == null ? "" : pct(sh);
});

const jsonClip = computed(() => {
  try { return JSON.stringify(rawValue.value).slice(0, 40); } catch { return "—"; }
});

const source = computed(() => {
  const s = props.envelope?.source;
  return typeof s === "string" && s ? s : "—";
});

const canSpark = computed(() => ["pct", "int", "days"].includes(props.def.kind));

const sparkSeries = computed<Array<number | null>>(() => {
  if (!canSpark.value) return [];
  const list = props.sprintsForSpark ?? [];
  return list.map((s) => {
    const env = (s.headline_metrics ?? {}) as Record<string, MetricEnvelope>;
    let v = env[props.def.id]?.value;
    if (v == null && props.def.id === "M7") {
      // Lambda flat-field fallback for M7 only.
      const flat = (s as unknown as Record<string, unknown>).m7_completion_pct;
      if (typeof flat === "number") v = flat;
    }
    return typeof v === "number" ? v : null;
  });
});

const hasSparkline = computed(
  () => canSpark.value && sparkSeries.value.some((v) => v != null),
);

const sparkStroke = computed(() => {
  switch (rag.value) {
    case "green": return "var(--rag-green, #15803d)";
    case "amber": return "var(--rag-amber, #b45309)";
    case "red":   return "var(--rag-red,   #991b1b)";
    default:      return "var(--text-muted, #6b7280)";
  }
});
</script>

<style scoped>
.metric-card {
  position: relative;
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 8px; padding: 12px 12px 14px; background: var(--surface-card, #fff);
  display: flex; flex-direction: column; gap: 4px;
  overflow: hidden;
}
.metric-card-head { display: flex; align-items: center; justify-content: space-between; }
.metric-id-cell { display: inline-flex; align-items: center; gap: 6px; }
.metric-id { font-size: 11px; color: var(--text-muted, #6b7280); }
.info-btn {
  width: 18px; height: 18px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid var(--surface-border, #e5e7eb);
  background: var(--surface-card, #fff); border-radius: 50%;
  color: var(--text-muted, #6b7280); cursor: pointer;
  padding: 0;
}
.info-btn:hover { color: var(--primary-color, #2563eb); border-color: var(--primary-color, #2563eb); }
.rag-chip {
  display: inline-flex; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600;
  border: 1px solid currentColor;
}
.rag-chip.green  { color: #15803d; background: #dcfce7; border-color: #86efac; }
.rag-chip.amber  { color: #92400e; background: #fef3c7; border-color: #fcd34d; }
.rag-chip.red    { color: #991b1b; background: #fee2e2; border-color: #fca5a5; }
.rag-chip.none   { color: #6b7280; background: #f3f4f6; border-color: #e5e7eb; }

.metric-label { font-size: 13px; color: var(--text-muted, #6b7280); }
.metric-value { font-size: 22px; font-weight: 600; line-height: 1.2; }
.metric-value.composite { font-size: 14px; font-weight: 500; color: var(--text-muted, #6b7280); }
.metric-value-block { display: flex; flex-direction: column; gap: 2px; }
.subtle { color: var(--text-muted, #6b7280); font-size: 13px; }
.subtle.d-suffix { margin-left: 4px; font-size: 14px; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }

.metric-spark { height: 28px; margin-top: 4px; color: var(--text-muted, #6b7280); }
.metric-spark-empty { background: none; }

.metric-source {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 11px; color: var(--text-muted, #6b7280);
  margin-top: 6px;
}
.rag-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.rag-dot.green { background: #16a34a; }
.rag-dot.amber { background: #d97706; }
.rag-dot.red   { background: #dc2626; }
.rag-dot.none  { background: #d1d5db; }

.metric-bar {
  position: absolute; left: 0; right: 0; bottom: 0; height: 3px;
}
.metric-bar.green { background: #16a34a; }
.metric-bar.amber { background: #d97706; }
.metric-bar.red   { background: #dc2626; }
.metric-bar.none  { background: transparent; }
</style>
